/**
 * Firebase initialization and configuration
 * Includes App Check for production security
 */

import { initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
import {
  getAuth,
  initializeAuth,
  GoogleAuthProvider,
  RecaptchaVerifier,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  setPersistence,
  AuthErrorCodes,
  type AuthError,
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getIOSSimulatorAppleSignInHint } from './platform';
import { getAnalytics, isSupported } from 'firebase/analytics';

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'parent-ai-companion.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'parent-ai-companion',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'parent-ai-companion.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '123456789',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:123456789:web:abc123',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-XXXXXXXX',
};

// Initialize Firebase app first
export const app = initializeApp(firebaseConfig);

// =============== App Check Initialization ===============
// App Check protects backend resources from abuse
// Uses reCAPTCHA v3 for web (invisible, score-based)
// Debug tokens enabled in development for localhost/127.0.0.1 testing

let appCheck: AppCheck | null = null;

/**
 * Initialize App Check before other Firebase services
 * - Production: Uses reCAPTCHA v3 provider with site key from env
 * - Development: Skipped entirely (backend bypasses enforcement in dev)
 * - Native iOS: Skipped (handled by native AppDelegate with DeviceCheck/AppAttest)
 */
function initAppCheck(): AppCheck | null {
  // Skip App Check for native Capacitor apps (iOS uses DeviceCheck/AppAttest via native SDK)
  const cap = (typeof window !== 'undefined'
    ? (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
    : null);

  if (cap?.isNativePlatform?.()) {
    console.log('[AppCheck] Skipped - native platform uses native providers');
    return null;
  }

  // In development: always skip App Check
  // Backend already bypasses App Check enforcement in dev/emulator mode
  if (import.meta.env.DEV) {
    console.log('[AppCheck] Skipped in development - backend bypasses enforcement');
    return null;
  }

  // Production: require reCAPTCHA Enterprise site key
  const siteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY;

  if (!siteKey) {
    console.warn('[AppCheck] No VITE_RECAPTCHA_ENTERPRISE_SITE_KEY configured - App Check disabled');
    return null;
  }

  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });

    console.log('[AppCheck] Initialized successfully');
    return appCheck;
  } catch (error) {
    console.warn('[AppCheck] Failed to initialize:', error);
    return null;
  }
}

// Initialize App Check BEFORE Auth to ensure tokens are ready
// This order is critical: App Check → Auth → Other services
appCheck = initAppCheck();

export { appCheck };

// Auth: on native use indexedDB persistence so web layer stays signed in after app restart
function getAuthInstance() {
  if (typeof window === 'undefined') return getAuth(app);
  const cap = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  if (cap?.isNativePlatform?.()) {
    try {
      // Native Capacitor: use indexedDB persistence for reliable cross-restart sessions
      return initializeAuth(app, { persistence: indexedDBLocalPersistence });
    } catch {
      // Fallback if initializeAuth fails (e.g., already initialized)
      return getAuth(app);
    }
  }
  // Web: use browserLocalPersistence via getAuth default + explicit setPersistence below
  return getAuth(app);
}
export const auth = getAuthInstance();

// =============== Firestore with offline persistence (non-deprecated API) ===============
// Use FirestoreSettings.localCache instead of deprecated enableMultiTabIndexedDbPersistence()
// Multi-tab IndexedDB when available; fallback to default (memory) on SSR or when persistence fails

/** Whether offline persistence is enabled (for UI indicators) */
export let firestorePersistenceEnabled = false;

/** Error if persistence failed (for debugging) */
export let firestorePersistenceError: Error | null = null;

function getFirestoreInstance(): ReturnType<typeof getFirestore> {
  if (typeof window === 'undefined') {
    return getFirestore(app);
  }
  try {
    const instance = initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
    firestorePersistenceEnabled = true;
    return instance;
  } catch (e) {
    firestorePersistenceError = e instanceof Error ? e : new Error(String(e));
    return getFirestore(app);
  }
}

export const db = getFirestoreInstance();
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Always use live Firebase endpoints (no emulators)
// Emulator connections have been removed - all environments use production Firebase

/**
 * Auth ready promise - resolves when Firebase Auth has finished restoring persisted session.
 * Use this to wait before making routing decisions to avoid redirect thrash.
 * Firebase Auth fires onAuthStateChanged synchronously with null if no session,
 * but needs time to rehydrate from IndexedDB/localStorage.
 */
export const authReady: Promise<void> = new Promise((resolve) => {
  // authStateReady() returns a promise that resolves when the initial auth state is determined
  // This is the official Firebase way to wait for session rehydration
  auth.authStateReady().then(() => resolve()).catch(() => resolve());
});

// Set auth persistence to local (survives browser restarts)
// This is async but Firebase queues operations so it's safe
// On native, we already use indexedDBLocalPersistence via initializeAuth
const cap = (typeof window !== 'undefined' ? (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor : null);
if (!cap?.isNativePlatform?.()) {
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.warn('Failed to set auth persistence:', error);
  });
}

// Analytics (only in production, check for browser support)
export const analytics = isSupported().then(supported => {
  if (supported && import.meta.env.PROD) {
    return getAnalytics(app);
  }
  return null;
});

// Google Auth Provider with enhanced configuration
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
});
// Request additional scopes if needed
googleProvider.addScope('email');
googleProvider.addScope('profile');

// =============== RecaptchaVerifier Management ===============
// Simplest approach: create verifier inline, let Firebase handle rendering

const RECAPTCHA_CONTAINER_ID = 'recaptcha-container';

/**
 * Ensure recaptcha container exists in DOM
 */
function ensureContainer(): HTMLElement {
  let container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = RECAPTCHA_CONTAINER_ID;
    document.body.appendChild(container);
  }
  return container;
}

/**
 * Create a fresh reCAPTCHA verifier for phone auth
 * Returns a new verifier each time - do NOT call render(), let signInWithPhoneNumber handle it
 */
export function initRecaptchaVerifier(): RecaptchaVerifier {
  ensureContainer();

  // Create new invisible reCAPTCHA verifier
  // Important: Do NOT pre-render - let Firebase handle it during signInWithPhoneNumber
  const verifier = new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, {
    size: 'invisible',
  });

  return verifier;
}

/**
 * Clear the reCAPTCHA verifier and clean up
 */
export function clearRecaptchaVerifier(): void {
  // Remove any orphaned reCAPTCHA widgets
  const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (container) {
    container.innerHTML = '';
  }
}

// =============== Auth Error Handling ===============

/**
 * User-friendly error messages for Firebase Auth errors
 * Maps technical error codes to helpful messages
 */
export function getAuthErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return 'An unexpected error occurred. Please try again.';
  }

  const authError = error as AuthError;
  const code = authError.code || (error as { code?: string }).code;
  const message = (authError.message || (error as Error).message || '').toLowerCase();

  // ─────────────────────────────────────────────────────────────────────────────
  // Message-based matching for errors without proper codes
  // These catch edge cases from reCAPTCHA, WebView issues, and native plugin errors
  // ─────────────────────────────────────────────────────────────────────────────

  // Check for reCAPTCHA/verifier failures (common in dev, misconfigured domains, or Capacitor WebView)
  if (message.includes("reading 'verify'") || message.includes('null')) {
    return 'Phone verification is temporarily unavailable. Please try Google Sign-In.';
  }

  // Catch the "Verification system error" that appears in Capacitor WebView
  // This happens when Firebase Web SDK's RecaptchaVerifier is used in capacitor://localhost
  if (message.includes('verification system error') || message.includes('please refresh the page')) {
    return 'Phone verification encountered an issue. Please try again or use a different sign-in method.';
  }

  // Catch generic reCAPTCHA failures that don't have proper error codes
  if (message.includes('recaptcha') && (message.includes('failed') || message.includes('error'))) {
    return 'Security verification failed. Please try again.';
  }

  // Catch generic phone verification failures from native plugin
  if (message.includes('phone verification failed') || message.includes('sms verification failed')) {
    return 'Unable to send verification code. Please check your phone number and try again.';
  }

  // Catch timeout errors
  if (message.includes('timeout') || message.includes('timed out')) {
    return 'Request timed out. Please check your connection and try again.';
  }

  // Catch reCAPTCHA authorization/configuration errors (401 from Google)
  if (message.includes('401') || message.includes('unauthorized')) {
    return 'Security verification failed. Please try again or use a different sign-in method.';
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Apple Sign-In errors (AuthenticationServices.AuthorizationError)
  // Error 1000 = cancelled by user, Error 1001 = failed, Error 1002 = invalid response
  // Error 1003 = not handled, Error 1004 = not interactive
  // ─────────────────────────────────────────────────────────────────────────────
  if (message.includes('authenticationservices.authorizationerror')) {
    if (message.includes('error 1000') || message.includes('code=1000')) {
      // User cancelled - this is expected behavior, don't show scary error
      return 'Sign-in was cancelled. Please try again.';
    }
    if (message.includes('error 1001') || message.includes('code=1001')) {
      return 'Apple Sign-In failed. Please try again.';
    }
    if (message.includes('error 1002') || message.includes('code=1002')) {
      return 'Invalid response from Apple. Please try again.';
    }
    if (message.includes('error 1003') || message.includes('code=1003')) {
      return 'Apple Sign-In is not available. Please try a different sign-in method.';
    }
    // Generic fallback for other Apple auth errors
    return 'Apple Sign-In encountered an issue. Please try again.';
  }

  // Also catch AKAuthenticationError (the underlying auth error)
  if (message.includes('akauthenticationerror')) {
    // -7022 is "unknown" error, often related to keychain or simulator issues
    if (message.includes('code=-7022') || message.includes('-7022')) {
      return 'Sign-in was cancelled. Please try again.';
    }
    return 'Apple Sign-In encountered an issue. Please try again.';
  }

  // Catch Firebase nonce/credential mismatch errors for Apple Sign-In
  if (message.includes('nonce') || message.includes('invalid_nonce')) {
    return 'Apple Sign-In verification failed. Please try again.';
  }

  // Catch "invalid id token" errors which can occur with Apple Sign-In misconfig
  if (message.includes('invalid') && message.includes('token')) {
    console.error('[Auth] Invalid token error - may indicate Apple Sign-In configuration issue');
    return 'Sign-in verification failed. Please try again.';
  }

  // Catch "missing credential" from Apple Sign-In (often simulator issue)
  if (message.includes('missing') && (message.includes('credential') || message.includes('idtoken'))) {
    console.error('[Auth] Missing credential - check if Apple ID is signed in on device/simulator');
    const simulatorHint = getIOSSimulatorAppleSignInHint();
    if (simulatorHint) {
      return 'Apple Sign-In failed. Please sign into an Apple ID in Simulator Settings → Apple ID.';
    }
    return 'Apple Sign-In failed. Please ensure you are signed into an Apple ID in Settings.';
  }

  switch (code) {
    // Network errors
    case AuthErrorCodes.NETWORK_REQUEST_FAILED:
      return 'Network error. Please check your connection and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';

    // Popup/redirect errors
    case AuthErrorCodes.POPUP_BLOCKED:
    case 'auth/popup-blocked':
      return 'Popup was blocked. Please allow popups or try another sign-in method.';
    case AuthErrorCodes.POPUP_CLOSED_BY_USER:
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled. Please try again.';
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled. Please try again.';

    // Phone auth errors
    case AuthErrorCodes.INVALID_CODE:
    case 'auth/invalid-verification-code':
      return 'Invalid verification code. Please check and try again.';
    case AuthErrorCodes.CODE_EXPIRED:
    case 'auth/code-expired':
      return 'Verification code expired. Please request a new code.';
    case 'auth/invalid-phone-number':
      return 'Invalid phone number. Please enter a valid US phone number.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'auth/missing-phone-number':
      return 'Please enter your phone number.';
    case 'auth/quota-exceeded':
      return 'SMS quota exceeded. Please try again later or use a different sign-in method.';

    // reCAPTCHA errors
    case 'auth/captcha-check-failed':
      return 'Security verification failed. Please refresh and try again.';
    case 'auth/recaptcha-not-enabled':
      return 'Phone authentication is not configured. Please contact support.';

    // Google sign-in errors
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email. Try signing in with a different method.';
    case 'auth/credential-already-in-use':
      return 'This account is already linked to another user.';
    case 'auth/email-already-in-use':
      return 'An account already exists with this email.';

    // Session/token errors
    case AuthErrorCodes.USER_DISABLED:
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case AuthErrorCodes.USER_DELETED:
    case 'auth/user-not-found':
      return 'Account not found. Please sign up first.';
    case 'auth/user-token-expired':
    case AuthErrorCodes.TOKEN_EXPIRED:
      return 'Your session has expired. Please sign in again.';
    case 'auth/requires-recent-login':
      return 'Please sign in again to continue.';

    // Internal errors
    case 'auth/internal-error':
      return 'An internal error occurred. Please try again.';
    case 'auth/invalid-api-key':
      return 'Configuration error. Please contact support.';

    // Web-specific errors
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for sign-in. Please contact support.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled. Please contact support.';

    // Cloud Functions errors (can occur during account operations)
    case 'functions/internal':
      return 'A server error occurred. Please try again later.';
    case 'functions/unavailable':
      return 'Service temporarily unavailable. Please try again later.';
    case 'functions/unauthenticated':
      return 'Please sign in again to continue.';

    default:
      // Log unknown errors for debugging (dev only)
      if (import.meta.env.DEV) {
        console.warn('Unknown auth error:', code, message);
      }
      // NEVER show raw Firebase error messages to users - they often contain
      // technical jargon like "please refresh the page" that doesn't apply to mobile apps
      // Instead, return a generic user-friendly message
      return 'Something went wrong. Please try again.';
  }
}

/**
 * Check if error is a network/connectivity error
 */
export function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = (error as { code?: string }).code;
  return code === AuthErrorCodes.NETWORK_REQUEST_FAILED ||
         code === 'auth/network-request-failed';
}

/**
 * Check if popup was blocked (to trigger redirect fallback)
 */
export function isPopupBlocked(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = (error as { code?: string }).code;
  return code === AuthErrorCodes.POPUP_BLOCKED || code === 'auth/popup-blocked';
}

// Warn if running on localhost (phone auth won't work)
if (import.meta.env.DEV && window.location.hostname === 'localhost') {
  console.warn('[Auth] Phone Auth requires http://127.0.0.1:5173 (not localhost)');
}
